/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

/**
 * Shared docked-rail resize handle for the right panel. Mounted by
 * `installRightPanelShell` onto EVERY right-panel surface (chat, queue), so
 * resizing is a property of the shell, not any one surface. A thin grabbable
 * strip on the panel's left edge (WIDE only; hidden by CSS on NARROW, where the
 * panel is fullscreen and has no rail). Drives the shared `rightPanelWidth$`,
 * which feeds the `--right-panel-width` var read by the panel and the
 * `#main-content` inset alike.
 *
 * WAI-ARIA window-splitter pattern: a focusable separator whose `aria-valuenow`
 * tracks the rail width; arrow keys nudge it, Home/End jump to max/min.
 */

import { jsx } from '@amazon/vinyl-tsx'
import { type MaybeObservableValue } from '@amazon/vinyl-observable'
import { createDisposer, type Unsubscribe } from '@amazon/vinyl-util'
import {
    MAX_RIGHT_PANEL_WIDTH,
    MIN_RIGHT_PANEL_WIDTH,
    rightPanelWidth$,
    setRightPanelWidth,
} from '@/rightPanel/rightPanelWidth'
import { pageZoom } from '@/util/domUtil'
import { isTv } from '@/device/deviceConfig'

// Keyboard resize step per Arrow keypress.
const RESIZE_KEY_STEP = 24

function RightPanelResizeHandle(props: { readonly label: MaybeObservableValue<string> }) {
    const tv = isTv()

    if (tv) {
        return <div className="rightPanelResizeHandle--hidden" data-testid="right-panel-resize" />
    }

    return (
        <div
            className="rightPanelResizeHandle focusRing"
            data-testid="right-panel-resize"
            role="separator"
            aria-orientation="vertical"
            tabIndex={0}
            aria-label={props.label}
            aria-valuemin={MIN_RIGHT_PANEL_WIDTH}
            aria-valuemax={MAX_RIGHT_PANEL_WIDTH}
            onConnect={(handle) => {
                const { add, dispose } = createDisposer()

                const applyAria = (width: number) => {
                    handle.setAttribute('aria-valuenow', String(Math.round(width)))
                }
                add(rightPanelWidth$.onData(applyAria))

                // Pointer drag. The handle is on the panel's LEFT edge and the
                // panel hugs the right, so dragging left WIDENS it: width =
                // startWidth - deltaX. Divide the screen-space delta by pageZoom()
                // before writing it into the fixed panel's width. Commit
                // (clamp + persist) via setRightPanelWidth on every move — its
                // no-op coalescing keeps storage churn down.
                let startX = 0
                let startWidth = 0
                let zoom = 1
                let dragging = false

                const onPointerMove = (event: PointerEvent) => {
                    if (!dragging) return
                    const delta = (event.clientX - startX) / zoom
                    setRightPanelWidth(startWidth - delta, window.innerWidth)
                }
                const endDrag = (event: PointerEvent) => {
                    if (!dragging) return
                    dragging = false
                    // Restore the width/inset transition now the drag is done so a
                    // subsequent programmatic width change animates.
                    document.body.classList.remove('rightPanelResizing')
                    if (handle.hasPointerCapture(event.pointerId)) {
                        handle.releasePointerCapture(event.pointerId)
                    }
                }
                const onPointerDown = (event: PointerEvent) => {
                    // Primary button / touch only.
                    if (event.button !== 0) return
                    event.preventDefault()
                    dragging = true
                    startX = event.clientX
                    startWidth = rightPanelWidth$.value
                    zoom = pageZoom()
                    // Suppress the rail-width + #main-content inset transitions for
                    // the drag so the panel tracks the pointer 1:1 instead of easing
                    // behind it (the ease is kept for programmatic width changes).
                    document.body.classList.add('rightPanelResizing')
                    handle.setPointerCapture(event.pointerId)
                }

                handle.addEventListener('pointerdown', onPointerDown)
                handle.addEventListener('pointermove', onPointerMove)
                handle.addEventListener('pointerup', endDrag)
                handle.addEventListener('pointercancel', endDrag)
                add(() => handle.removeEventListener('pointerdown', onPointerDown))
                add(() => handle.removeEventListener('pointermove', onPointerMove))
                add(() => handle.removeEventListener('pointerup', endDrag))
                add(() => handle.removeEventListener('pointercancel', endDrag))
                // Belt-and-suspenders: if the panel tears down mid-drag, drop the
                // transition-suppression class so it can't leak onto body.
                add(() => document.body.classList.remove('rightPanelResizing'))

                // Keyboard: Left widens / Right narrows (mirrors drag-left = wider);
                // Home/End jump to max/min. Each commits + persists.
                const onHandleKey = (event: KeyboardEvent) => {
                    let next: number | undefined
                    if (event.key === 'ArrowLeft') next = rightPanelWidth$.value + RESIZE_KEY_STEP
                    else if (event.key === 'ArrowRight')
                        next = rightPanelWidth$.value - RESIZE_KEY_STEP
                    else if (event.key === 'Home') next = MAX_RIGHT_PANEL_WIDTH
                    else if (event.key === 'End') next = MIN_RIGHT_PANEL_WIDTH
                    if (next == null) return
                    event.preventDefault()
                    setRightPanelWidth(next, window.innerWidth)
                }
                handle.addEventListener('keydown', onHandleKey)
                add(() => handle.removeEventListener('keydown', onHandleKey))

                return dispose
            }}
        />
    )
}

/**
 * Prepend the shared resize handle to a right-panel surface element and return a
 * disposer that removes it (tearing down its listeners via the connected-observer
 * disconnect). Called by `installRightPanelShell` for every surface.
 */
export function installRightPanelResizeHandle(
    el: HTMLElement,
    opts: { readonly label: MaybeObservableValue<string> }
): Unsubscribe {
    const handle = <RightPanelResizeHandle label={opts.label} />
    el.prepend(handle)
    return () => handle.remove()
}
